import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Contact, EmailThread } from "../../api/types";
import { useWorkspaceLens } from "../../app/workspaceLens";
import { Card, EditorPage, ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { DetailPageSkeleton } from "../../ui/skeletons";
import { marketingStateLabels, useContact, useContacts } from "../contacts/queries";
import {
  emptyDraft,
  type MessageDraft,
  MessageFields,
  scheduleToISO,
  templatesForPurpose,
} from "./MessageFields";
import { useEmailTemplates, useMailboxes } from "./queries";

// Opening a conversation asks three questions in order: who it is with, what kind of message it
// is, and what it says. The purpose comes second because it decides whether this person may
// lawfully be written to at all, and which templates are even offered.
//
// There used to be a list of recipients here, ticked from the contacts at an account. A customer
// is one person now, so the recipient is not a choice the sender makes and not one they can get
// wrong. What replaced the list is the reason a send would be refused, said before it is tried.
export function ComposePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  // Every venue the membership can see rather than the one in the top bar: the person being
  // written to is the same person whichever venue they last hired.
  const contacts = useContacts(search, "active", "");
  const templates = useEmailTemplates();
  const [contactId, setContactId] = useState(params.get("contactId") ?? "");
  const [purpose, setPurpose] = useState("service");
  const mailboxes = useMailboxes();
  const lens = useWorkspaceLens();
  const [mailboxId, setMailboxId] = useState("");
  // A mailbox can be kept for one half of the building, and the open workspace says which half
  // this message is being written from. Until the sender chooses otherwise, the message leaves
  // from that half's mailbox rather than the organisation-wide default.
  const activeMailboxes = (mailboxes.data?.items ?? []).filter((mb) => mb.status === "active");
  const workspaceMailbox = activeMailboxes.find((mb) => mb.purpose === lens.workspace);
  const chosenMailbox = mailboxId || workspaceMailbox?.id || "";
  const [draft, setDraft] = useState<MessageDraft>(emptyDraft);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  // The list is one page long. Without this, a workspace holding more contacts than that page
  // could not write to the ones past the end of it, and a contact opened from their own record
  // would sit chosen with nothing showing as selected. Reading the chosen contact by their own
  // identifier keeps them among the options whatever the search happens to be.
  const chosen = useContact(contactId || undefined);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setWorking(true);
    setError(undefined);
    try {
      const thread = await api<EmailThread>("/api/v1/messaging/threads", {
        method: "POST",
        body: jsonBody({
          customerId: contactId,
          purpose,
          templateKey: draft.templateKey,
          subject: draft.templateKey ? "" : draft.subject,
          textBody: draft.templateKey ? "" : draft.textBody,
          mailboxId: chosenMailbox || undefined,
          scheduledAt: scheduleToISO(draft.scheduledAt),
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["messaging", "threads"] });
      navigate(`/emails/${thread.id}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (contacts.isLoading)
    return <DetailPageSkeleton title="New message" backTo="/emails" backLabel="Back to emails" />;
  if (contacts.error) return <ErrorPanel error={contacts.error} />;

  const available = templatesForPurpose(templates.data?.items ?? [], purpose);
  const listed = contacts.data?.items ?? [];
  const current = chosen.data;
  const options = current ? [current, ...listed.filter((item) => item.id !== current.id)] : listed;
  const refusal = sendRefusal(current, purpose);

  return (
    <EditorPage
      title="New message"
      description="Opens a conversation with one customer. It is recorded against their record, so anybody picking it up later can see what was said."
      backTo="/emails"
      backLabel="Back to emails"
    >
      <Card>
        {error ? <ErrorPanel error={error} /> : null}
        <form className="form-grid" onSubmit={send}>
          <Field
            label="Find contact"
            hint="Type a name or address if the person you want is not in the list below."
          >
            <input
              type="search"
              aria-label="Find contact"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, email, or organization"
            />
          </Field>
          <Field label="Contact">
            <select
              name="customerId"
              required
              value={contactId}
              onChange={(event) => setContactId(event.target.value)}
            >
              <option value="">Choose a contact</option>
              {options.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.organisation
                    ? `${contact.displayName} (${contact.organisation})`
                    : contact.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Purpose"
            hint="Service covers anything about a booking. Marketing needs recorded consent from this person and cannot be mixed into a service conversation."
          >
            <select
              name="purpose"
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
            >
              <option value="service">Service</option>
              <option value="marketing">Marketing</option>
            </select>
          </Field>
          {(mailboxes.data?.items.length ?? 0) > 1 ? (
            <Field
              label="Send from"
              hint={
                workspaceMailbox
                  ? `Starts on the ${lens.name.toLowerCase()} mailbox. Choose another to send from it instead.`
                  : "Choose which mailbox this message is sent from. Leave on the default if you only have one."
              }
            >
              <select
                name="mailboxId"
                value={chosenMailbox}
                onChange={(event) => setMailboxId(event.target.value)}
              >
                <option value="">Default mailbox</option>
                {activeMailboxes.map((mb) => (
                  <option key={mb.id} value={mb.id}>
                    {mb.displayName ? `${mb.displayName} (${mb.address})` : mb.address}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {current ? (
            <div className="full">
              {refusal ? (
                <Notice tone="warning" title="This message cannot be sent">
                  {refusal}
                </Notice>
              ) : (
                <Notice tone="info">
                  Going to {current.email}
                  {purpose === "marketing"
                    ? `, who consented: ${current.marketingEmail.source || "source not recorded"}.`
                    : "."}
                </Notice>
              )}
            </div>
          ) : null}
          <MessageFields templates={available} draft={draft} onChange={setDraft} />
          <div className="full">
            <FormActions
              submitting={working}
              disabled={!contactId || Boolean(refusal)}
              submitLabel={draft.scheduledAt ? "Schedule message" : "Send message"}
              onCancel={() => navigate("/emails")}
            />
          </div>
        </form>
      </Card>
    </EditorPage>
  );
}

// The server refuses each of these outright. Naming the one that applies, before the button is
// pressed, is the difference between a rule and a message that mysteriously will not send.
function sendRefusal(contact: Contact | undefined, purpose: string): string {
  if (!contact) return "";
  if (contact.status !== "active") {
    return "This contact is archived, so they are not written to. Restore them first if this conversation needs to happen.";
  }
  if (!contact.email) {
    return "This contact has no email address, so there is nowhere to send. Add one on their record first.";
  }
  if (purpose === "marketing" && contact.marketingEmail.state !== "granted") {
    const state = marketingStateLabels[contact.marketingEmail.state];
    return state
      ? `Marketing needs recorded consent, and this contact is marked "${state}".`
      : "Marketing needs recorded consent, and nobody has asked this contact yet.";
  }
  return "";
}
